import React, { useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import {
  FaHome, FaSearch, FaUserTag, FaUsers, FaHotel, FaUserTie, FaBuilding, FaCogs,
  FaFileInvoice, FaCreditCard, FaMoneyBillWave, FaCheckCircle, FaCity, FaFileExport,
  FaFlag, FaFileContract, FaCog, FaCommentDots, FaUserCircle, FaShieldAlt, FaHeadset,
} from 'react-icons/fa';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import './admin-theme.css';
import Overview from './components/Overview';
import DataExports from './components/DataExports';
import Landlords from './components/Landlords';
import LandlordDetails from './components/LandlordDetails';
import AirbnbHosts from './components/AirbnbHosts';
import AirbnbHostDetails from './components/AirbnbHostDetails';
import Agents from './components/Agents';
import AgentDetails from './components/AgentDetails';
import Properties from './components/Properties';
import PropertyDetails from './components/PropertyDetails';

import Payments from './components/Payments';
import Plans from './components/Plans';
import KYCManagement from './components/KYCManagement';
import KYCDetails from './components/KYCDetails';
import KYBManagement from './components/KYBManagement';
import RoleRequests from './components/RoleRequests';
import RoleRequestDetails from './components/RoleRequestDetails';
import Subscriptions from './components/Subscriptions';
import SystemSettings from './components/SystemSettings';
import UserSearch from './components/UserSearch';
import UserAgreement from './components/UserAgreement';
import UnitConfig from './components/UnitConfig';
import Reports from './components/Reports';
import AuditLogs from './components/AuditLogs';
import Notifications from './components/Notifications';
import Profile from '../Profile/Profile';
import Messages from '../shared/Messages/Messages';

const sidebarItems = [
  { id: 'overview', label: 'Dashboard', icon: <FaHome />, section: 'MANAGEMENT' },
  { id: 'user-search', label: 'User Search', icon: <FaSearch />, section: 'MANAGEMENT' },
  { id: 'role-requests', label: 'Role Requests', icon: <FaUserTag />, section: 'MANAGEMENT' },
  { id: 'landlords', label: 'Landlords', icon: <FaUsers />, section: 'MANAGEMENT' },
  { id: 'airbnb-hosts', label: 'Airbnb Hosts', icon: <FaHotel />, section: 'MANAGEMENT' },
  { id: 'agents', label: 'Agents', icon: <FaUserTie />, section: 'MANAGEMENT' },
  { id: 'properties', label: 'Properties', icon: <FaBuilding />, section: 'MANAGEMENT' },
  { id: 'unit-config', label: 'Unit Config', icon: <FaCogs />, section: 'MANAGEMENT' },
  { id: 'plans', label: 'Plans', icon: <FaFileInvoice />, section: 'FINANCE' },
  { id: 'subscriptions', label: 'Subscriptions', icon: <FaCreditCard />, section: 'FINANCE' },
  { id: 'payments', label: 'Payments', icon: <FaMoneyBillWave />, section: 'FINANCE' },
  { id: 'kyc', label: 'KYC Verification', icon: <FaCheckCircle />, section: 'TRUST & SECURITY' },
  { id: 'kyb', label: 'KYB Verification', icon: <FaCity />, section: 'TRUST & SECURITY' },
  { id: 'reports', label: 'Reports', icon: <FaFlag />, section: 'TRUST & SECURITY' },
  { id: 'audit-logs', label: 'Audit Logs', icon: <FaShieldAlt />, section: 'TRUST & SECURITY' },
  { id: 'data-exports', label: 'Data Exports', icon: <FaFileExport />, section: 'SYSTEM' },
  { id: 'user-agreement', label: 'User Agreement', icon: <FaFileContract />, section: 'SYSTEM' },

  { id: 'system-settings', label: 'System Settings', icon: <FaCog />, section: 'SYSTEM' },
  { id: 'notifications', label: 'Notifications', icon: <FaCommentDots />, section: 'COMMUNICATION' },
  { id: 'messages', label: 'Support Chats', icon: <FaHeadset />, section: 'COMMUNICATION' },
  { id: 'profile', label: 'My Profile', icon: <FaUserCircle />, section: 'SYSTEM' },
];

const SystemAdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive the active section from the URL (first path segment under /system-admin/).
  const activeSection = useMemo(() => {
    const parts = location.pathname.replace(/^\/system-admin\/?/, '').split('/');
    return parts[0] || 'overview';
  }, [location.pathname]);

  useScrollToTop(activeSection);

  const handleNavigate = (section, state = {}) => {
    navigate(`/system-admin/${section}`, { state });
  };

  return (
    <div className="admin-shell">
      <div className="dashboard">
        <Sidebar
          items={sidebarItems}
          activeItem={activeSection}
          onItemClick={handleNavigate}
          variant="admin"
        />
        <div className="dashboard-content">
          <Header
            userRole="System Admin"
            activeSection={activeSection}
            onNavClick={handleNavigate}
          />
          <main className="dashboard-main">
            <Routes>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<Overview onNavigate={handleNavigate} />} />
              <Route path="user-search" element={<UserSearch />} />
              <Route path="role-requests" element={<RoleRequests />} />
              <Route path="role-requests/:requestId" element={<RoleRequestDetails />} />
              <Route path="landlords" element={<Landlords />} />
              <Route path="landlords/:landlordId" element={<LandlordDetails />} />
              <Route path="airbnb-hosts" element={<AirbnbHosts />} />
              <Route path="airbnb-hosts/:hostId" element={<AirbnbHostDetails />} />
              <Route path="agents" element={<Agents />} />
              <Route path="agents/:agentId" element={<AgentDetails />} />
              <Route path="properties" element={<Properties />} />
              <Route path="properties/:propertyId" element={<PropertyDetails />} />
              <Route path="unit-config" element={<UnitConfig />} />

              <Route path="plans" element={<Plans />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="payments" element={<Payments />} />
              <Route path="kyc" element={<KYCManagement />} />
              <Route path="kyc/:kycId" element={<KYCDetails />} />
              <Route path="kyb" element={<KYBManagement />} />
              {/* Analytics is merged into the Dashboard (Overview) as a tab. */}
              <Route path="analytics" element={<Navigate to="../overview" replace />} />
              <Route path="data-exports" element={<DataExports />} />
              <Route path="reports" element={<Reports />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="notifications" element={<Notifications />} />

              <Route path="user-agreement" element={<UserAgreement />} />
              <Route path="system-settings" element={<SystemSettings />} />
              <Route path="profile" element={<Profile />} />
              <Route
                path="messages"
                element={<MessagesRouteWrapper />}
              />
              <Route path="*" element={<Navigate to="overview" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

/** Reads optional threadId from router state for the Messages section. */
const MessagesRouteWrapper = () => {
  const location = useLocation();
  const threadId = location.state?.threadId;
  return <Messages userRole="Admin" threadId={threadId} />;
};

export default SystemAdminDashboard;
